import React from "react";

import {
  Box,
  Typography,
  TextField,
  Grid,
  FormControlLabel,
  Checkbox,
  RadioGroup,
  Radio,
  InputAdornment,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { useFormContext, Controller } from "react-hook-form";
import { makeStyles, withStyles } from "tss-react/mui";

import { GestationalKind } from "@/gql/graphql";
import { colors } from "@/theme/overrides/colors";

import { FormValues } from "./EditMedicalHistoryModal";

const useStyles = makeStyles()({
  error: {
    fontSize: "12px",
    textAlign: "center",
    color: "#963832",
  },
  checkboxLabel: {
    color: colors.grey,
  },
});

const StyledTextField = withStyles(TextField, {
  root: {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#F7F9FB",
    },
    width: "100%",
    marginTop: "16px",
  },
});

const PregnancyHistoryFields: React.FC = () => {
  const { classes } = useStyles();
  const { control, watch } = useFormContext<FormValues>();
  const isCurrentlyPregnant = watch("pregnancyHistoryFields.currentlyPregnant");
  const isCurrentlyLactating = watch(
    "pregnancyHistoryFields.currentlyLactating"
  );

  return (
    <>
      <Grid item xs={!isCurrentlyPregnant && isCurrentlyLactating ? 12 : 6}>
        <Typography>Select items that apply to you:</Typography>
        <Box marginBottom={3} />
        <Box display="flex" alignItems="center">
          <Controller
            name="pregnancyHistoryFields.currentlyPregnant"
            control={control}
            render={({ field: { onChange, value } }) => {
              return (
                <FormControlLabel
                  className={classes.checkboxLabel}
                  control={
                    <Checkbox
                      color="primary"
                      checked={value}
                      onChange={onChange}
                    />
                  }
                  label="Currently pregnant"
                />
              );
            }}
          />
          <Box marginX={3} />
          <Controller
            name="pregnancyHistoryFields.currentlyLactating"
            control={control}
            render={({ field: { onChange, value } }) => {
              return (
                <FormControlLabel
                  className={classes.checkboxLabel}
                  control={
                    <Checkbox
                      color="primary"
                      checked={value}
                      onChange={onChange}
                    />
                  }
                  label="Currently lactating"
                />
              );
            }}
          />
        </Box>
      </Grid>
      {isCurrentlyPregnant && (
        <>
          <Controller
            name="pregnancyHistoryFields.gestationalType"
            control={control}
            render={({ field: { onChange, value }, fieldState: { error } }) => {
              return (
                <Grid item xs={12} lg={12}>
                  <Typography>Gestational Type</Typography>
                  <Box marginBottom={3} />
                  <RadioGroup
                    name="gestationalType"
                    onChange={onChange}
                    value={value}
                  >
                    <Box display="flex" alignItems="center">
                      <FormControlLabel
                        value={GestationalKind.Single}
                        control={<Radio color="primary" />}
                        label="Single"
                      />
                      <Box marginX={3} />
                      <FormControlLabel
                        value={GestationalKind.Multiple}
                        control={<Radio color="primary" />}
                        label="Multiple"
                      />
                    </Box>
                  </RadioGroup>
                  <Typography variant="subtitle1" className={classes.error}>
                    {error?.message}
                  </Typography>
                </Grid>
              );
            }}
          />
          <Controller
            name="pregnancyHistoryFields.lastMenstruationOn"
            control={control}
            render={({
              field: { onChange, name, ref, ...field },
              fieldState: { error },
            }) => (
              <Grid item xs={12} lg={6}>
                <Typography>Start of Last Menstruation</Typography>
                <DatePicker
                  {...field}
                  inputRef={ref}
                  onChange={(value) => onChange(value as Date)}
                  slotProps={{
                    textField: {
                      name,
                      fullWidth: true,
                      error: !!error,
                    },
                  }}
                />
                <Typography variant="subtitle1" className={classes.error}>
                  {error?.message}
                </Typography>
              </Grid>
            )}
          />
          <Controller
            name="pregnancyHistoryFields.numberOfPregnancies"
            control={control}
            render={({ field: { onChange, value }, fieldState: { error } }) => {
              return (
                <Grid item xs={12} lg={6}>
                  <Typography>No. of Pregnancies</Typography>
                  <StyledTextField
                    type="number"
                    value={value}
                    onChange={onChange}
                  />
                  <Typography variant="subtitle1" className={classes.error}>
                    {error?.message}
                  </Typography>
                </Grid>
              );
            }}
          />
          <Controller
            name="pregnancyHistoryFields.pregnancyObservation"
            control={control}
            render={({ field: { onChange, value }, fieldState: { error } }) => {
              return (
                <Grid item xs={12} lg={6}>
                  <Typography>Pregnancy Observation</Typography>
                  <StyledTextField
                    multiline
                    value={value}
                    onChange={onChange}
                  />
                  <Typography variant="subtitle1" className={classes.error}>
                    {error?.message}
                  </Typography>
                </Grid>
              );
            }}
          />
        </>
      )}
      {isCurrentlyLactating && (
        <>
          <Controller
            name="pregnancyHistoryFields.lactationStartsOn"
            control={control}
            render={({
              field: { onChange, name, ref, ...field },
              fieldState: { error },
            }) => (
              <Grid item xs={12} lg={6}>
                <Typography>Start of Lactation</Typography>
                <DatePicker
                  {...field}
                  inputRef={ref}
                  onChange={(value) => onChange(value as Date)}
                  slotProps={{
                    textField: {
                      name,
                      fullWidth: true,
                      error: !!error,
                    },
                  }}
                />
                <Typography variant="subtitle1" className={classes.error}>
                  {error?.message}
                </Typography>
              </Grid>
            )}
          />
          <Controller
            name="pregnancyHistoryFields.lactationDurationMonths"
            control={control}
            render={({ field: { onChange, value }, fieldState: { error } }) => {
              return (
                <Grid item xs={12} lg={6}>
                  <Typography>Lactation duration</Typography>
                  <StyledTextField
                    type="number"
                    defaultValue={value}
                    onChange={onChange}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          {value === 1 ? "month" : "months"}
                        </InputAdornment>
                      ),
                    }}
                  />
                  <Typography variant="subtitle1" className={classes.error}>
                    {error?.message}
                  </Typography>
                </Grid>
              );
            }}
          />
        </>
      )}
    </>
  );
};
export default PregnancyHistoryFields;
